import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@mui/material";
import intl from "react-intl-universal";
import { TbCopy, TbKey } from "react-icons/tb";
import { ModalCloseButton } from "../../../../../Components/Molecules/ValutDialog";
import { useToast } from "../../../../../Hooks/toast";
import {
  isBaasHmlInstance,
  isBaasInstanceReleased,
} from "../../../../../Services/baasInstancesApi";

const inputClass =
  "h-10 w-full rounded-lg border border-gray-200 px-3 text-sm text-brand-dark outline-none transition focus:border-[#e7a73f] dark:border-brand-dark-border dark:bg-brand-dark-elevated dark:text-gray-100";

export const BaasApiKeyCreateModal = ({
  open,
  onClose,
  onSubmit,
  submitting = false,
  instance = null,
}) => {
  const { addToast } = useToast();
  const [name, setName] = useState("");
  const [environment, setEnvironment] = useState("sandbox");
  const [createdKey, setCreatedKey] = useState(null);
  const [formError, setFormError] = useState(null);

  const isHml = isBaasHmlInstance(instance);
  const prodLiberated = isBaasInstanceReleased(instance);

  const environmentOptions = useMemo(() => {
    if (isHml) {
      return [{ value: "sandbox", disabled: false }];
    }
    return [
      { value: "sandbox", disabled: false },
      { value: "production", disabled: !prodLiberated },
    ];
  }, [isHml, prodLiberated]);

  useEffect(() => {
    if (!open) return;
    setEnvironment(isHml || !prodLiberated ? "sandbox" : "production");
    setFormError(null);
  }, [open, isHml, prodLiberated]);

  const resetForm = () => {
    setName("");
    setCreatedKey(null);
    setFormError(null);
    setEnvironment(isHml || !prodLiberated ? "sandbox" : "production");
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!name.trim()) return;

    if (isHml && environment === "production") {
      setFormError(intl.get("baas.api.keys.createProdOnHmlError"));
      return;
    }
    if (!isHml && environment === "production" && !prodLiberated) {
      setFormError(intl.get("baas.api.keys.createProdNotLiberatedError"));
      return;
    }

    const result = await onSubmit({
      name: name.trim(),
      environment,
    });

    if (result?.fullKey) {
      setCreatedKey(result);
      setFormError(null);
    }
  };

  const handleCopy = async () => {
    if (!createdKey?.fullKey) return;
    try {
      await navigator.clipboard.writeText(createdKey.fullKey);
      setFormError(null);
      addToast({
        type: "success",
        title: intl.get("baas.api.keys.copied"),
      });
    } catch {
      setFormError(intl.get("baas.processing.copyError"));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ className: "!rounded-2xl !shadow-card" }}
    >
      {createdKey ? (
        <div className="px-5 py-6 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#e7a73f]/15">
              <TbKey className="h-5 w-5 text-[#a66f1d]" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-brand-dark dark:text-gray-100">
                {intl.get("baas.api.keys.createdTitle")}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {intl.get("baas.api.keys.createdSubtitle")}
              </p>
            </div>
          </div>

          {formError ? (
            <p
              role="alert"
              className="mt-4 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
            >
              {formError}
            </p>
          ) : null}

          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50/80 p-4 dark:border-amber-900/40 dark:bg-amber-950/20">
            <p className="text-xs font-medium text-amber-800 dark:text-amber-300">
              {intl.get("baas.api.keys.copyWarning")}
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="min-w-0 flex-1 break-all rounded-lg bg-white px-3 py-2 text-xs text-brand-dark dark:bg-brand-dark-elevated dark:text-gray-100">
                {createdKey.fullKey}
              </code>
              <button type="button" className="btn-outline !h-9 !min-h-9 !px-3" onClick={handleCopy}>
                <TbCopy className="h-4 w-4" />
              </button>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button type="button" className="btn-primary" onClick={handleClose}>
              {intl.get("words.close")}
            </button>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="relative px-5 py-6 sm:px-6">
          <ModalCloseButton onClick={handleClose} className="absolute right-3 top-3" disabled={submitting} />
          <h2 className="pr-8 text-lg font-bold text-brand-dark dark:text-gray-100">
            {intl.get("baas.api.keys.createTitle")}
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {intl.get("baas.api.keys.createSubtitle")}
          </p>

          <div className="mt-5 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.api.keys.nameLabel")}
              </label>
              <input
                className={inputClass}
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder={intl.get("baas.api.keys.namePlaceholder")}
                required
              />
            </div>

            <div>
              <p className="mb-1.5 text-xs font-medium text-gray-500 dark:text-gray-400">
                {intl.get("baas.api.filters.environment")}
              </p>
              {isHml ? (
                <>
                  <p className="text-sm text-brand-dark dark:text-gray-100">
                    {intl.get("baas.api.environment.sandbox")} (HML)
                  </p>
                  <p className="mt-1 text-[11px] text-gray-400">
                    {intl.get("baas.api.keys.environmentHintHml")}
                  </p>
                </>
              ) : (
                <>
                  <div className="flex flex-wrap gap-2">
                    {environmentOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        disabled={option.disabled || submitting}
                        onClick={() => setEnvironment(option.value)}
                        className={`rounded-lg border px-3 py-2 text-sm transition ${
                          environment === option.value
                            ? "border-[#e7a73f] bg-[#e7a73f]/15 font-semibold text-brand-dark dark:text-gray-100"
                            : "border-gray-200 text-gray-600 dark:border-brand-dark-border dark:text-gray-300"
                        } ${option.disabled ? "cursor-not-allowed opacity-50" : ""}`}
                      >
                        {option.value === "production"
                          ? intl.get("baas.api.environment.production")
                          : `${intl.get("baas.api.environment.sandbox")} (HML)`}
                      </button>
                    ))}
                  </div>
                  <p className="mt-1 text-[11px] text-gray-400">
                    {prodLiberated
                      ? intl.get("baas.api.keys.environmentHintProd")
                      : intl.get("baas.api.keys.environmentHintProdBlocked")}
                  </p>
                </>
              )}
            </div>

            {formError ? (
              <p
                role="alert"
                className="rounded-xl border border-red-200 bg-red-50/80 px-4 py-3 text-sm text-red-700 dark:border-red-900/50 dark:bg-red-950/30 dark:text-red-300"
              >
                {formError}
              </p>
            ) : null}
          </div>

          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="submit"
              className="btn-primary"
              disabled={submitting || !name.trim()}
            >
              {intl.get("baas.api.keys.generate")}
            </button>
          </div>
        </form>
      )}
    </Dialog>
  );
};
